import React, { useState } from 'react';
import { Search, Filter, MoreVertical, Star } from 'lucide-react';
import { properties, formatNaira } from '../../data/mockData';
import { VerificationBadge, TierBadge } from '../../components/shared/Badges';
export const ListingManagement = () => {
  const [statusFilter, setStatusFilter] = useState<'all' | 'approved' | 'pending' | 'reported'>('all');
  const [search, setSearch] = useState('');
  const filteredProperties = properties.filter(p => {
    const matchesStatus = statusFilter === 'all' || statusFilter === 'approved' && p.verificationStatus === 'verified' || statusFilter === 'pending' && p.verificationStatus === 'pending' || statusFilter === 'reported' && (p.verificationStatus === 'rejected' || p.verificationStatus === 'reported');
    const matchesSearch = search === '' || p.title.toLowerCase().includes(search.toLowerCase()) || p.district.toLowerCase().includes(search.toLowerCase());
    return matchesStatus && matchesSearch;
  });
  const tabs: Array<{
    key: 'all' | 'approved' | 'pending' | 'reported';
    label: string;
  }> = [{
    key: 'all',
    label: 'All'
  }, {
    key: 'approved',
    label: 'Approved'
  }, {
    key: 'pending',
    label: 'Pending'
  }, {
    key: 'reported',
    label: 'Reported'
  }];
  return <div className="space-y-6 animate-in fade-in" data-id="element-747">
      <div data-id="element-748">
        <h1 className="text-2xl font-serif font-bold text-slate-900 dark:text-white mb-1" data-id="element-749">
          Listing Management
        </h1>
        <p className="text-slate-500 dark:text-slate-400" data-id="element-750">
          Manage all properties on the platform.
        </p>
      </div>

      <div className="bg-white dark:bg-[#1e293b] rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden" data-id="element-751">
        <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex flex-col sm:flex-row gap-4 justify-between items-center bg-slate-50 dark:bg-slate-800/50" data-id="element-752">
          <div className="flex gap-2 overflow-x-auto w-full sm:w-auto pb-2 sm:pb-0 custom-scrollbar" data-id="element-753">
            {tabs.map(tab => <button key={tab.key} onClick={() => setStatusFilter(tab.key)} className={`px-4 py-1.5 rounded-lg text-sm font-bold whitespace-nowrap transition-colors ${statusFilter === tab.key ? 'bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-white' : 'text-slate-500 dark:text-slate-400 font-medium hover:bg-slate-100 dark:hover:bg-slate-800'}`} data-id="element-754">
                {tab.label}
              </button>)}
          </div>
          <div className="flex gap-2 w-full sm:w-auto" data-id="element-755">
            <div className="relative flex-grow sm:w-64" data-id="element-756">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" size={16} data-id="element-757" />
              <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search listings..." className="w-full pl-9 pr-4 py-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white text-sm outline-none focus:ring-2 focus:ring-secondary" data-id="element-758" />
            </div>
          </div>
        </div>

        <div className="overflow-x-auto" data-id="element-759">
          <table className="w-full text-left border-collapse min-w-[800px]" data-id="element-760">
            <thead data-id="element-761">
              <tr className="bg-slate-50 dark:bg-slate-900/50 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider border-b border-slate-200 dark:border-slate-700" data-id="element-762">
                <th className="p-4 pl-6" data-id="element-763">Property</th>
                <th className="p-4" data-id="element-764">Seller</th>
                <th className="p-4" data-id="element-765">Status</th>
                <th className="p-4" data-id="element-766">Tier</th>
                <th className="p-4" data-id="element-767">Price</th>
                <th className="p-4 pr-6 text-right" data-id="element-768">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700/50" data-id="element-769">
              {filteredProperties.length === 0 ? <tr data-id="element-770">
                  <td colSpan={6} className="p-8 text-center text-sm text-slate-500 dark:text-slate-400" data-id="element-771">
                    No listings match your filters.
                  </td>
                </tr> : filteredProperties.map(p => <tr key={p.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors" data-id="element-772">
                    <td className="p-4 pl-6" data-id="element-773">
                      <div className="flex items-center gap-3" data-id="element-774">
                        <img src={p.images[0]} alt="" className="w-10 h-10 rounded-lg object-cover" data-id="element-775" />
                        <div data-id="element-776">
                          <div className="font-bold text-sm text-slate-900 dark:text-slate-200 line-clamp-1 flex items-center gap-2" data-id="element-777">
                            {p.title}
                            {p.tier === 1 && <Star size={12} className="text-secondary fill-secondary" data-id="element-778" />}
                          </div>
                          <div className="text-xs text-slate-500 dark:text-slate-400" data-id="element-779">
                            {p.district}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-sm text-slate-600 dark:text-slate-300" data-id="element-780">
                      {p.seller.name}
                    </td>
                    <td className="p-4" data-id="element-781">
                      <VerificationBadge status={p.verificationStatus as any} data-id="element-782" />
                    </td>
                    <td className="p-4" data-id="element-783">
                      <TierBadge tier={p.tier as 1 | 2 | 3} data-id="element-784" />
                    </td>
                    <td className="p-4 font-medium text-sm text-slate-900 dark:text-slate-200" data-id="element-785">
                      {formatNaira(p.price)}
                    </td>
                    <td className="p-4 pr-6 text-right" data-id="element-786">
                      <button className="p-2 text-slate-400 hover:text-slate-900 dark:hover:text-white rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors" data-id="element-787">
                        <MoreVertical size={18} data-id="element-788" />
                      </button>
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
};